import { useState, useMemo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Search, BookOpen, Sparkles, ChevronRight, ArrowLeft } from 'lucide-react'
import { HELP_ARTICLES, HELP_CATEGORIAS, type HelpArticle } from '@/data/helpArticles'

export default function AjudaPage() {
  const [params, setParams] = useSearchParams()
  const [busca, setBusca] = useState('')
  const [catFiltro, setCatFiltro] = useState<string | null>(null)
  const artigoIdSel = params.get('art')

  const artigoSel = useMemo(
    () => HELP_ARTICLES.find(a => a.id === artigoIdSel) || null,
    [artigoIdSel],
  )

  const filtrados = useMemo(() => {
    let arr = HELP_ARTICLES
    if (catFiltro) arr = arr.filter(a => a.categoria === catFiltro)
    if (busca.trim()) {
      const q = busca.toLowerCase()
      arr = arr.filter(a =>
        a.titulo.toLowerCase().includes(q) ||
        a.resumo.toLowerCase().includes(q) ||
        a.tags.some(t => t.toLowerCase().includes(q)) ||
        a.conteudo.toLowerCase().includes(q)
      )
    }
    return arr
  }, [busca, catFiltro])

  function abrir(art: HelpArticle) {
    setParams({ art: art.id })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Escuta links cross-referência [texto](#id) dentro dos artigos
  useEffect(() => {
    const handler = (e: Event) => {
      const id = (e as CustomEvent).detail as string
      const a = HELP_ARTICLES.find(x => x.id === id)
      if (a) abrir(a)
    }
    window.addEventListener('open-help-art', handler)
    return () => window.removeEventListener('open-help-art', handler)
  }, [])

  function abrirChat() {
    // dispara evento global que o Layout escuta pra abrir o AiChat
    window.dispatchEvent(new CustomEvent('open-ai-chat'))
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-violet-600" /> Central de Ajuda
          </h1>
          <p className="text-sm text-ink-500 mt-1">
            Tutoriais passo a passo + chat com a Sofia pra dúvidas específicas
          </p>
        </div>
        <button
          onClick={abrirChat}
          className="px-4 py-2 bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 text-white rounded-lg flex items-center gap-2 font-medium shadow-md"
        >
          <Sparkles className="h-4 w-4" /> Pergunte à Sofia
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* SIDEBAR */}
        <aside className="bg-white border border-ink-200 rounded-lg p-3 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-100px)] lg:overflow-y-auto">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar..."
              className="w-full h-10 pl-9 pr-3 border border-ink-200 rounded-lg text-sm"
            />
          </div>

          <div className="space-y-1">
            <button
              onClick={() => setCatFiltro(null)}
              className={`w-full text-left px-2 py-1.5 rounded text-sm font-medium ${
                catFiltro === null ? 'bg-violet-100 text-violet-800' : 'hover:bg-ink-50'
              }`}
            >
              📚 Todas categorias
            </button>
            {HELP_CATEGORIAS.map(c => {
              const count = HELP_ARTICLES.filter(a => a.categoria === c.id).length
              if (count === 0) return null
              return (
                <button
                  key={c.id}
                  onClick={() => setCatFiltro(catFiltro === c.id ? null : c.id)}
                  className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center justify-between ${
                    catFiltro === c.id ? 'bg-violet-100 text-violet-800 font-bold' : 'hover:bg-ink-50'
                  }`}
                >
                  <span>{c.icone} {c.label}</span>
                  <span className="text-xs text-ink-400">{count}</span>
                </button>
              )
            })}
          </div>

          <div className="border-t border-ink-100 mt-3 pt-3 space-y-1 max-h-[400px] overflow-y-auto">
            <div className="text-xs uppercase text-ink-500 font-semibold mb-1 px-2">
              Artigos {filtrados.length > 0 && `(${filtrados.length})`}
            </div>
            {filtrados.map(a => (
              <button
                key={a.id}
                onClick={() => abrir(a)}
                className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-1 ${
                  artigoIdSel === a.id ? 'bg-blue-50 text-blue-800 font-medium' : 'hover:bg-ink-50'
                }`}
              >
                <span>{a.icone}</span>
                <span className="line-clamp-2">{a.titulo}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* CONTEÚDO */}
        <main className="bg-white border border-ink-200 rounded-lg p-6 min-h-[60vh]">
          {!artigoSel ? (
            <Cards artigos={filtrados} onClick={abrir} />
          ) : (
            <ArtigoView
              art={artigoSel}
              onClose={() => setParams({})}
              onAbrirArtigo={(id) => {
                const art = HELP_ARTICLES.find(a => a.id === id)
                if (art) abrir(art)
              }}
              onAbrirChat={abrirChat}
            />
          )}
        </main>
      </div>
    </div>
  )
}


function Cards({ artigos, onClick }: { artigos: HelpArticle[]; onClick: (a: HelpArticle) => void }) {
  if (artigos.length === 0) {
    return <div className="text-center py-12 text-ink-500">
      <BookOpen className="h-12 w-12 mx-auto mb-2 opacity-30" />
      Nenhum artigo encontrado
    </div>
  }
  // agrupa por categoria
  const porCat: Record<string, HelpArticle[]> = {}
  artigos.forEach(a => {
    porCat[a.categoria] = porCat[a.categoria] || []
    porCat[a.categoria].push(a)
  })

  return (
    <div className="space-y-6">
      {HELP_CATEGORIAS.map(c => {
        const items = porCat[c.id] || []
        if (items.length === 0) return null
        return (
          <section key={c.id}>
            <h2 className="text-base font-bold mb-3 flex items-center gap-2">
              <span>{c.icone}</span> {c.label}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {items.map(a => (
                <button
                  key={a.id}
                  onClick={() => onClick(a)}
                  className="bg-ink-50 hover:bg-violet-50 border border-ink-200 hover:border-violet-300 rounded-lg p-4 text-left transition group"
                >
                  <div className="font-bold text-sm mb-1 flex items-start gap-1">
                    <span>{a.icone}</span>
                    <span className="flex-1">{a.titulo}</span>
                    <ChevronRight className="h-4 w-4 text-ink-400 group-hover:text-violet-600 transition shrink-0" />
                  </div>
                  <div className="text-xs text-ink-600">{a.resumo}</div>
                </button>
              ))}
            </div>
          </section>
        )
      })}
    </div>
  )
}


function ArtigoView({
  art, onClose, onAbrirArtigo, onAbrirChat,
}: {
  art: HelpArticle
  onClose: () => void
  onAbrirArtigo: (id: string) => void
  onAbrirChat: () => void
}) {
  const html = useMemo(() => mdToHtml(art.conteudo, onAbrirArtigo), [art.id])

  function baixarPDF() {
    // Salva título atual e troca pra título do artigo (vira nome do PDF)
    const tituloOriginal = document.title
    document.title = art.titulo.replace(/[^\w\s-]/g, '').slice(0, 80) + ' — VELORE ERP'
    // Marca o body com classe pra CSS @print esconder o que não é artigo
    document.body.classList.add('printing-help-article')
    setTimeout(() => {
      window.print()
      document.body.classList.remove('printing-help-article')
      document.title = tituloOriginal
    }, 50)
  }

  return (
    <article className="help-article-print-target">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4 print:hidden">
        <button onClick={onClose} className="text-sm text-ink-500 hover:text-ink-900 flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Voltar pros artigos
        </button>
        <button
          onClick={baixarPDF}
          className="text-sm px-3 py-1.5 rounded-lg bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-600 hover:to-orange-600 text-white font-semibold flex items-center gap-1.5 shadow-sm"
          title="Imprimir / Salvar como PDF">
          📄 Baixar PDF
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4 text-xs print:hidden">
        {art.tags.slice(0, 5).map(t => (
          <span key={t} className="px-2 py-0.5 bg-violet-50 text-violet-700 rounded font-mono">#{t}</span>
        ))}
      </div>

      <div
        className="md-content"
        dangerouslySetInnerHTML={{ __html: html }}
      />

      <div className="mt-8 pt-6 border-t border-ink-200 bg-violet-50 -mx-6 -mb-6 px-6 py-4 rounded-b-lg">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-bold text-violet-900">Não achou o que procurava?</div>
            <div className="text-xs text-violet-700 mt-0.5">Pergunta direto pra Sofia — ela conhece o sistema todo</div>
          </div>
          <button
            onClick={onAbrirChat}
            className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
          >
            <Sparkles className="h-4 w-4" /> Abrir chat
          </button>
        </div>
      </div>

      <style>{`
        .md-content h1 { font-size: 1.5rem; font-weight: 800; margin-bottom: 1rem; color: #0f172a; }
        .md-content h2 { font-size: 1.15rem; font-weight: 700; margin-top: 1.5rem; margin-bottom: 0.5rem; color: #1e293b; padding-bottom: 0.25rem; border-bottom: 1px solid #e2e8f0; }
        .md-content h3 { font-size: 1rem; font-weight: 700; margin-top: 1rem; margin-bottom: 0.5rem; color: #334155; }
        .md-content p { margin-bottom: 0.75rem; line-height: 1.6; color: #334155; }
        .md-content ul, .md-content ol { margin-left: 1.25rem; margin-bottom: 0.75rem; }
        .md-content ul { list-style: disc; }
        .md-content ol { list-style: decimal; }
        .md-content li { margin-bottom: 0.25rem; line-height: 1.55; color: #334155; }
        .md-content code { background: #f1f5f9; padding: 1px 5px; border-radius: 3px; font-size: 0.85em; font-family: monospace; color: #7c3aed; }
        .md-content pre { background: #0f172a; color: #e2e8f0; padding: 0.75rem; border-radius: 8px; overflow-x: auto; margin-bottom: 0.75rem; font-size: 0.85em; }
        .md-content pre code { background: transparent; color: inherit; padding: 0; }
        .md-content strong { font-weight: 700; color: #0f172a; }
        .md-content table { width: 100%; border-collapse: collapse; margin: 0.75rem 0; }
        .md-content table th, .md-content table td { border: 1px solid #e2e8f0; padding: 6px 10px; text-align: left; font-size: 0.9em; }
        .md-content table th { background: #f8fafc; font-weight: 700; }
        .md-content a { color: #7c3aed; text-decoration: underline; cursor: pointer; }
        .md-content a:hover { color: #5b21b6; }
        .md-content blockquote { border-left: 3px solid #7c3aed; padding-left: 0.75rem; color: #64748b; margin: 0.75rem 0; }
        .md-content .help-flow { display: flex; flex-wrap: wrap; align-items: stretch; gap: 0; margin: 1.1rem 0; }
        .md-content .help-flow-step { flex: 1 1 150px; min-width: 140px; background: linear-gradient(135deg,#faf5ff,#eff6ff); border: 1px solid #ddd6fe; border-radius: 14px; padding: 0.65rem 0.8rem; display: flex; gap: 0.55rem; align-items: flex-start; box-shadow: 0 1px 3px rgba(124,58,237,.08); }
        .md-content .help-flow-num { flex: none; width: 24px; height: 24px; border-radius: 50%; background: linear-gradient(135deg,#7c3aed,#ec4899); color:#fff; font-size:0.78rem; font-weight:800; display:flex; align-items:center; justify-content:center; box-shadow: 0 2px 6px rgba(124,58,237,.35); }
        .md-content .help-flow-title { font-weight: 700; font-size: 0.86rem; color:#1e293b; line-height:1.25; }
        .md-content .help-flow-desc { font-size: 0.72rem; color:#64748b; margin-top:2px; line-height:1.3; }
        .md-content .help-flow-arrow { display:flex; align-items:center; justify-content:center; color:#a78bfa; font-weight:800; font-size:1.1rem; padding: 0 6px; }
        @media (max-width: 640px){ .md-content .help-flow{ flex-direction: column; } .md-content .help-flow-arrow{ transform: rotate(90deg); padding: 4px 0; } }
      `}</style>
    </article>
  )
}


// Markdown→HTML mínimo (sem dep externa)
function mdToHtml(md: string, onAbrirArtigo: (id: string) => void): string {
  const escapeHtml = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

  // Tabelas (simples) — dois passes
  const lines = md.split('\n')
  const out: string[] = []
  let i = 0
  let inCodeBlock = false
  let codeLang = ''
  let codeBuffer: string[] = []

  while (i < lines.length) {
    const line = lines[i]

    // Flow block: ::: flow  ...  :::  (1 etapa por linha; "Título :: descrição" opcional)
    // Renderiza cards numerados conectados por setas (visual, pra leigos).
    const flowMatch = line.match(/^:::\s*flow\s*$/)
    if (flowMatch) {
      const buf: string[] = []
      i++
      while (i < lines.length && lines[i].trim() !== ':::') { buf.push(lines[i]); i++ }
      i++ // pula :::
      const steps = buf.map(s => s.trim()).filter(Boolean)
      const stepsHtml = steps.map((s, idx) => {
        const partes = s.split('::')
        const titulo = (partes[0] || '').trim()
        const desc = (partes[1] || '').trim()
        return `<div class="help-flow-step"><span class="help-flow-num">${idx + 1}</span>` +
          `<div class="help-flow-txt"><div class="help-flow-title">${parseInline(titulo, onAbrirArtigo)}</div>` +
          (desc ? `<div class="help-flow-desc">${parseInline(desc, onAbrirArtigo)}</div>` : '') +
          `</div></div>`
      }).join('<div class="help-flow-arrow">➜</div>')
      out.push(`<div class="help-flow">${stepsHtml}</div>`)
      continue
    }

    // Callout block: ::: tipo  ...  :::
    // tipos: tip | warning | danger | info | success
    const calloutMatch = line.match(/^:::\s*(tip|warning|danger|info|success|step)(?:\s+(.*))?$/)
    if (calloutMatch) {
      const tipo = calloutMatch[1]
      const titulo = calloutMatch[2] || ''
      const buf: string[] = []
      i++
      while (i < lines.length && lines[i].trim() !== ':::') {
        buf.push(lines[i]); i++
      }
      i++ // pula :::
      const inner = mdToHtml(buf.join('\n'), onAbrirArtigo)
      const icones: Record<string, string> = {
        tip: '💡', warning: '⚠️', danger: '🚫', info: 'ℹ️', success: '✅', step: '📌',
      }
      const cores: Record<string, string> = {
        tip: 'callout callout-tip',
        warning: 'callout callout-warning',
        danger: 'callout callout-danger',
        info: 'callout callout-info',
        success: 'callout callout-success',
        step: 'callout callout-step',
      }
      out.push(
        `<div class="${cores[tipo]}">` +
        `<div class="callout-head"><span class="callout-icon">${icones[tipo]}</span>` +
        (titulo ? `<span class="callout-title">${escapeHtml(titulo)}</span>` : '') +
        `</div><div class="callout-body">${inner}</div></div>`
      )
      continue
    }

    // Code block
    if (line.startsWith('```')) {
      if (!inCodeBlock) {
        inCodeBlock = true
        codeLang = line.slice(3).trim()
        codeBuffer = []
      } else {
        out.push(`<pre><code class="lang-${codeLang}">${escapeHtml(codeBuffer.join('\n'))}</code></pre>`)
        inCodeBlock = false
      }
      i++; continue
    }
    if (inCodeBlock) {
      codeBuffer.push(line); i++; continue
    }

    // Tabela: linha começa com |
    if (line.trim().startsWith('|') && i + 1 < lines.length && /^\|[\s:-]+\|/.test(lines[i + 1])) {
      const headerCells = line.split('|').slice(1, -1).map(c => c.trim())
      i += 2  // pula header + separador
      const rows: string[][] = []
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        rows.push(lines[i].split('|').slice(1, -1).map(c => c.trim()))
        i++
      }
      out.push('<table><thead><tr>' + headerCells.map(c => `<th>${parseInline(c, onAbrirArtigo)}</th>`).join('') + '</tr></thead><tbody>')
      rows.forEach(r => {
        out.push('<tr>' + r.map(c => `<td>${parseInline(c, onAbrirArtigo)}</td>`).join('') + '</tr>')
      })
      out.push('</tbody></table>')
      continue
    }

    // Heading
    if (line.startsWith('# ')) { out.push(`<h1>${parseInline(line.slice(2), onAbrirArtigo)}</h1>`); i++; continue }
    if (line.startsWith('## ')) { out.push(`<h2>${parseInline(line.slice(3), onAbrirArtigo)}</h2>`); i++; continue }
    if (line.startsWith('### ')) { out.push(`<h3>${parseInline(line.slice(4), onAbrirArtigo)}</h3>`); i++; continue }

    // Listas
    if (/^[\-*] /.test(line.trim())) {
      const ulItems: string[] = []
      while (i < lines.length && /^[\-*] /.test(lines[i].trim())) {
        ulItems.push(lines[i].trim().slice(2))
        i++
      }
      out.push('<ul>' + ulItems.map(it => `<li>${parseInline(it, onAbrirArtigo)}</li>`).join('') + '</ul>')
      continue
    }
    if (/^\d+\. /.test(line.trim())) {
      const olItems: string[] = []
      while (i < lines.length && /^\d+\. /.test(lines[i].trim())) {
        olItems.push(lines[i].trim().replace(/^\d+\. /, ''))
        i++
      }
      out.push('<ol>' + olItems.map(it => `<li>${parseInline(it, onAbrirArtigo)}</li>`).join('') + '</ol>')
      continue
    }

    // Linha vazia
    if (line.trim() === '') { i++; continue }

    // Parágrafo
    out.push(`<p>${parseInline(line, onAbrirArtigo)}</p>`)
    i++
  }

  // Substitui links cross-referência [texto](#id) por anchor que abre artigo
  return out.join('\n')
}


function parseInline(text: string, onAbrirArtigo: (id: string) => void): string {
  let s = text
  // Escape < > primeiro
  s = s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  // Bold **text**
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  // Code `text`
  s = s.replace(/`([^`]+)`/g, '<code>$1</code>')
  // Link [text](#article-id) — abre artigo via callback
  s = s.replace(/\[([^\]]+)\]\(#([^)]+)\)/g, (_, txt, id) =>
    `<a data-art="${id}" onclick="window.dispatchEvent(new CustomEvent('open-help-art', {detail: '${id}'}))">${txt}</a>`
  )
  return s
}


// Hook global pra capturar clicks em links de artigo
export function useHelpLinks(onAbrir: (id: string) => void) {
  useEffect(() => {
    const handler = (e: Event) => {
      const id = (e as CustomEvent).detail as string
      onAbrir(id)
    }
    window.addEventListener('open-help-art', handler)
    return () => window.removeEventListener('open-help-art', handler)
  }, [onAbrir])
}
